import React, { useState } from 'react';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import { useAlert } from '../../../../../../context/AlertContext';
import ledgerImportServices from '../ledgerImportServices';

function LedgerImportPage() {
    const [file, setFile] = useState(null);
    const [saving, setSavingStatus] = useState(false);
    const [responseMessage, setResponseMessage] = useState(null);
    const [errors, setErrors] = useState([]);
    const [error, setError] = useState("");
    const [jsonData, setJsonData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Extracted States
    const [extractedGroup, setExtractedGroup] = useState([]);
    const [extractedLedger, setExtractedLedger] = useState([]);

    // Upload Step Tracking States
    const [groupUploadStatus, setGroupUploadStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'
    const [ledgerUploadStatus, setLedgerUploadStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'

    // Progress tracking states for chunk uploading ledgers
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [progressPercentage, setProgressPercentage] = useState(0);

    const { AppAlert } = useAlert();

    // Helper to clean Tally control characters like \u0004
    const cleanTallyText = (text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(/\\u[0-9a-fA-F]{4}|\u0004|[\u0000-\u001F\u007F-\u009F]/g, '').trim();
    };

    // Helper function to extract Groups and Ledgers from Tally JSON
    const extractTallyData = (data) => {
        const groups = [];
        const ledgers = [];

        if (!data || !data.tallymessage) return { groups, ledgers };

        data.tallymessage.forEach(item => {
            const type = item.metadata?.type;
            const name = cleanTallyText(item.metadata?.name);


            if (type === "Group" && name) {
                const exists = groups.some(group => group.name === name && group.parent === item.parent);
                if (!exists) {
                    groups.push({ name: name, parent: item.parent });
                }
            } else if (type === "Ledger") {
                
                // 1. Map all mailing details into an array of address objects
                const addresses = Array.isArray(item.ledmailingdetails) 
                    ? item.ledmailingdetails.map(detail => {
                        let rawAddress = detail.address;
                        let formattedAddress = "";
                        if (Array.isArray(rawAddress)) {
                            formattedAddress = rawAddress
                                .filter(line => typeof line === 'string')
                                .map(line => cleanTallyText(line))
                                .join(', ');
                        } else if (typeof rawAddress === 'string') {
                            formattedAddress = cleanTallyText(rawAddress);
                        }

                        return {
                            addressLine: formattedAddress,
                            mailingname: cleanTallyText(detail.mailingname || ""),
                            city: cleanTallyText(detail.city || ""),
                            state: cleanTallyText(detail.state || ""),
                            pincode: cleanTallyText(detail.pincode || ""),
                            country: cleanTallyText(detail.country || ""),
                            applicablefrom: cleanTallyText(detail.applicablefrom || "")
                        };
                    }) 
                    : [];

                // 2. Map all GST registration details into an array
                const gstDetails = Array.isArray(item.ledgstregdetails)
                    ? item.ledgstregdetails.map(gst => ({
                        gstregistrationtype: cleanTallyText(gst.gstregistrationtype || ""),
                        state: cleanTallyText(gst.state || ""),
                        placeofsupply: cleanTallyText(gst.placeofsupply || ""),
                        gstin: cleanTallyText(gst.gstin || ""),
                        applicablefrom: cleanTallyText(gst.applicablefrom || "")
                    }))
                    : [];

                // 3. Push structured ledger object
                ledgers.push({
                    name: name || "",
                    email:cleanTallyText(item.email||""),
                    phone:cleanTallyText(item.ledgerphone||""),
                    contactPerson:cleanTallyText(item.ledgercontact||""),
                    mobile: cleanTallyText(item.ledgermobile||""),
                    parent: cleanTallyText(item.parent || ""),
                    partygstin: cleanTallyText(item.partygstin || ""),
                    currencyname: cleanTallyText(item.currencyname || ""),
                    creditLimit: cleanTallyText(item.creditlimit || ""),
                    openingbalance: cleanTallyText(item.openingbalance || "0.00"),
                    istdsapplicable: item.istdsapplicable || false,
                    address: addresses,
                    gstDetails: gstDetails
                });
            }
        });

        return { groups, ledgers };
    };

    // Handle file selection and parsing
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile); 
        setResponseMessage(null);
        setErrors([]);
        setError("");
        setJsonData(null);
        setExtractedGroup([]);
        setExtractedLedger([]);
        setGroupUploadStatus('idle');
        setLedgerUploadStatus('idle');

        if (!selectedFile) return;

        if (selectedFile.type !== "application/json" && !selectedFile.name.endsWith('.json')) {
            setError('Please select a valid JSON file.');
            AppAlert('Please select a valid JSON file.', 'error');
            return;
        }

        setIsLoading(true);
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const parsedData = JSON.parse(event.target.result);
                setJsonData(parsedData); 

                const { groups, ledgers } = extractTallyData(parsedData);
                setExtractedGroup(groups);
                setExtractedLedger(ledgers);
            } catch (err) {
                setError('Failed to parse JSON. Please check file formatting.');
                AppAlert('Failed to parse JSON.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        reader.onerror = () => {
            setError('Error reading the file.');
            setIsLoading(false);
        };
        reader.readAsText(selectedFile);
    };

    // Two-part Upload Script: 1. Groups (in one go) -> 2. Ledgers (in chunks)
    const handleUploadToServer = async () => {
        if (!extractedGroup.length && !extractedLedger.length) return;

        setSavingStatus(true);
        setError("");
        setResponseMessage(null);

        try {
            // PART 1: Upload All Groups in One Go
            if (extractedGroup.length > 0) {
                setGroupUploadStatus('uploading');
                await new Promise((resolve, reject) => {
                    ledgerImportServices.importTallyLedgerGroups(extractedGroup)
                        .then(res => {
                            setGroupUploadStatus('success');
                            resolve(res);
                        })
                        .catch(err => {
                            setGroupUploadStatus('error');
                            console.log("errors",err);
                            reject(err);
                        });
                });
            } else {
                setGroupUploadStatus('success'); // Skip cleanly if no groups
            }

            // PART 2: Upload Ledgers in Chunks
            if (extractedLedger.length > 0) {
                setLedgerUploadStatus('uploading');
                const totalItems = extractedLedger.length;
                const CHUNK_SIZE = 1000;
                let uploadedCount = 0;

                setUploadProgress({ current: 0, total: totalItems });
                setProgressPercentage(0);

                for (let i = 0; i < totalItems; i += CHUNK_SIZE) {
                    const chunk = extractedLedger.slice(i, i + CHUNK_SIZE);
                    
                    await new Promise((resolve, reject) => {
                        ledgerImportServices.importTallyLedgers({ ledgers: chunk })
                            .then(res => {
                                uploadedCount += chunk.length;
                                setUploadProgress({ current: uploadedCount, total: totalItems });
                                setProgressPercentage(Math.round((uploadedCount / totalItems) * 100));
                                resolve(res);
                            })
                            .catch(err => {
                                reject(err);
                            });
                    });
                }
                setLedgerUploadStatus('success');
            } else {
                setLedgerUploadStatus('success');
            }

            AppAlert("Tally Data Import successful!");
            setResponseMessage('Groups and Ledgers imported successfully!');

        } catch (err) {
            console.log(err);
            setError(err?.msg || 'Upload failed');
            AppAlert(err?.msg || "Upload failed", "error");
        } finally {
            setSavingStatus(false);
        }
    };

    return (
        <FinanceLayout>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <h1>Ledger Import</h1>
                        <div className="mb-3">
                            <label htmlFor="formFile" className="form-label">Upload JSON File</label>
                            <input className="form-control" type="file" id="formFile" accept=".json" onChange={handleFileChange} />
                        </div>
                        
                        {error && <div className="alert alert-danger">{error}</div>}
                        {responseMessage && <div className="alert alert-success">{responseMessage}</div>}
                        
                        <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={handleUploadToServer}
                            disabled={saving || isLoading || (!extractedGroup?.length && !extractedLedger?.length)}
                        >
                            {saving ? 'Processing Import...' : `Upload (${extractedGroup?.length || 0} Groups, ${extractedLedger?.length || 0} Ledgers)`}
                        </button>

                        {/* Step 1 Status Indicator: Groups */}
                        {groupUploadStatus !== 'idle' && (
                            <div className="mt-3 d-flex align-items-center">
                                <span className="me-2 fw-bold">1. Ledger Groups:</span>
                                {groupUploadStatus === 'uploading' && (
                                    <span>
                                        <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>
                                        Updating Groups...
                                    </span>
                                )}
                                {groupUploadStatus === 'success' && (
                                    <span className="text-success fw-bold">
                                        ✓ Groups Updated Successfully
                                    </span>
                                )}
                                {groupUploadStatus === 'error' && (
                                    <span className="text-danger">Failed to update groups</span>
                                )}
                            </div>
                        )}

                        {/* Step 2 Status Indicator & Progress Bar: Ledgers */}
                        {(ledgerUploadStatus !== 'idle' || groupUploadStatus === 'success') && (
                            <div className="mt-3">
                                <div className="d-flex align-items-center mb-1">
                                    <span className="me-2 fw-bold">2. Ledgers:</span>
                                    {ledgerUploadStatus === 'uploading' && <span className="text-muted">Uploading records in batches...</span>}
                                    {ledgerUploadStatus === 'success' && <span className="text-success fw-bold">✓ Ledgers Uploaded Successfully</span>}
                                    {ledgerUploadStatus === 'idle' && groupUploadStatus === 'success' && <span className="text-muted">Waiting to upload...</span>}
                                </div>
                                
                                {ledgerUploadStatus === 'uploading' && (
                                    <>
                                        <div className="d-flex justify-content-between mb-1">
                                            <small>{uploadProgress.current} / {uploadProgress.total} records</small>
                                            <small>{progressPercentage}%</small>
                                        </div>
                                        <div className="progress" style={{ height: '20px' }}>
                                            <div 
                                                className="progress-bar progress-bar-striped progress-bar-animated" 
                                                role="progressbar" 
                                                style={{ width: `${progressPercentage}%` }}
                                                aria-valuenow={progressPercentage} 
                                                aria-valuemin="0" 
                                                aria-valuemax="100"
                                            >
                                                {progressPercentage}%
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className='row'>
                    {isLoading && (
                        <div className="col-12 text-center" style={{ marginTop: '20px' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Reading and parsing JSON file...</p>
                        </div>
                    )}

                    {!isLoading && jsonData && (
                        <div className="col-12 mt-4">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="p-3 bg-light rounded">
                                        <h5>Extracted Groups ({extractedGroup?.length || 0}):</h5>
                                        <pre style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {JSON.stringify(extractedGroup, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="p-3 bg-light rounded">
                                        <h5>Extracted Ledgers Preview ({extractedLedger?.length || 0}):</h5>
                                        <pre style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            {JSON.stringify(extractedLedger, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </FinanceLayout>
    );
}

export default LedgerImportPage;